import * as React from "react";
import Radium from "radium";
import { styles } from "./google-btn.styles";

import withStyles, { CSSProperties } from "@material-ui/core/styles/withStyles";
import { Button as MUIButton, MenuItem, WithStyles } from "@material-ui/core";
import { altLoginButton } from "@state/local-storage";
import { googleDarkNormal, googleDarkDisabled } from "@img/google";
import { SvgIconComponent } from "@material-ui/icons";
import { Button } from "@clockwise/design-system";

interface Props extends WithStyles<typeof styles> {
  labelColor?: string;
  primary?: boolean;
  onSuccess: (response: any) => any;
  onFailure: (error: any) => any;
  label: string;
  disabled?: boolean;
  onClick?: (e: React.TouchEvent<any>) => void;
  isMenuItem?: boolean;
  flat?: boolean;
  googleStandard?: boolean;
  style?: CSSProperties;
  labelStyle?: CSSProperties;
  fullWidth?: boolean;
  icon?: SvgIconComponent;
  className?: string;
  excludeAdminPermissions?: boolean;
  excludeContactPermission?: boolean;
}

type State = {
  ready: boolean;
};
@Radium
class GoogleBtn extends React.Component<Props, State> {
  private auth2: any;

  constructor(props: Props) {
    super(props);
    this.state = {
      ready: false,
    };
  }

  public UNSAFE_componentWillMount() {
    this.listenForGoogleLoaded();
  }

  private listenForGoogleLoaded() {
    const gapi = (window as any).gapi;
    if (!!gapi && !!gapi.load) {
      this.initGoogleAuth();
    } else {
      window.addEventListener("google-loaded", this.initGoogleAuth.bind(this));
    }
  }

  private initGoogleAuth() {
    const gapi = (window as any).gapi;
    gapi.load("auth2", {
      onerror: this.props.onFailure,
      callback: () => {
        let client_id;
        switch (process.env.NODE_ENV) {
          case "staging":
            client_id = "744331920389-v2aaelb6eoirta1hqmeglpsu2lbrithp.apps.googleusercontent.com";
            break;
          case "production":
            client_id = "813252810479-k5kd9g4f569a3efhdptsdb70vdff4oda.apps.googleusercontent.com";
            break;
          default:
            client_id = "550663439598-uphe42i7mu6pms7vit5hi4ivud8o1cgq.apps.googleusercontent.com";
            break;
        }
        // Retrieve the singleton for the GoogleAuth library and set up the client.
        this.auth2 = gapi.auth2.init({
          client_id,
          cookiepolicy: "single_host_origin",
        });

        // catch any errors related to auth2 init, particularly idpiframe_initialization_failed
        this.auth2.then(undefined, this.props.onFailure);

        this.setState({ ready: true });
        window.removeEventListener("google-loaded", this.initGoogleAuth);
      },
    });
  }

  private btnClick(event: React.TouchEvent<any>) {
    this.props.onClick?.(event);

    // monkey patch window.open (temporarily!) to resize the height to accommodate scopes
    // but if we've previously failed, dont try again
    if (!altLoginButton.get()) {
      const winOpen = window.open;
      window.open = function () {
        let win;
        try {
          win = winOpen.apply(this, arguments);
        } catch (e) {
          // if winOpen fails, dont monkey patch in the future
          altLoginButton.set(true);
          window.open = winOpen;
          return window.open(...arguments);
        }

        try {
          win.resizeTo(600, 870);
        } catch (e) {
          // nop, the 2nd time you click the button for an existing window, it will be cross origin and throw an error
        }

        // un-monkey patch
        window.open = winOpen;
        return win;
      };
    }

    const isSafari = window.navigator.userAgent.toLowerCase().indexOf("safari") !== -1;

    const baseScope = `\
        https://www.googleapis.com/auth/calendar \
        https://www.googleapis.com/auth/userinfo.email \
        https://www.googleapis.com/auth/userinfo.profile${
          !this.props.excludeContactPermission
            ? " \
        https://www.googleapis.com/auth/contacts.readonly"
            : ""
        }`;

    const adminScope =
      " \
        https://www.googleapis.com/auth/admin.directory.user.readonly \
        https://www.googleapis.com/auth/admin.directory.resource.calendar.readonly";

    const scope = this.props.excludeAdminPermissions ? baseScope : baseScope + adminScope;

    this.auth2
      .grantOfflineAccess({
        scope,
        redirect_uri: "postmessage",
        approval_prompt: !isSafari ? "force" : undefined,
        prompt: !isSafari ? undefined : "consent",
      })
      .then(this.props.onSuccess, this.props.onFailure);
  }

  public render() {
    if (this.props.isMenuItem) {
      return (
        <MenuItem
          style={this.props.style}
          disabled={!this.state.ready || !!this.props.disabled}
          onClick={this.btnClick.bind(this)}
        >
          {this.props.label}
        </MenuItem>
      );
    } else if (this.props.flat) {
      return (
        <Button
          variant="text"
          startIcon={this.props.icon}
          onClick={this.btnClick.bind(this)}
          disabled={!this.state.ready || !!this.props.disabled}
          fullWidth={!!this.props.fullWidth}
        >
          {this.props.label}
        </Button>
      );
    } else if (this.props.googleStandard) {
      return (
        <MUIButton
          variant="contained"
          className={this.props.classes.googleButton}
          style={{ ...this.props.style }}
          TouchRippleProps={{ classes: { ripple: this.props.classes.rippleRoot } }}
          disabled={!this.state.ready || !!this.props.disabled}
          onClick={this.btnClick.bind(this)}
          fullWidth={!!this.props.fullWidth}
        >
          <img
            style={{ marginLeft: 0 }}
            src={!this.state.ready || !!this.props.disabled ? googleDarkDisabled : googleDarkNormal}
          />
          <span style={{ ...this.props.labelStyle, color: this.props.labelColor }}>
            {this.props.label}
          </span>
        </MUIButton>
      );
    } else {
      return (
        <Button
          variant="outlined"
          startIcon={this.props.icon}
          disabled={!this.state.ready || !!this.props.disabled}
          onClick={this.btnClick.bind(this)}
          fullWidth={!!this.props.fullWidth}
        >
          {this.props.label}
        </Button>
      );
    }
  }
}

const GoogleBtnStyled = withStyles(styles)(GoogleBtn);

export default GoogleBtnStyled;
